import {
  OnboardingItem,
  ProjectDepartment,
  ProjectTask,
  TaskPriority,
  TaskStatus,
  TaskGroup,
} from '../types';

/** Prefix for mirrored checklist task ids */
const TASK_PREFIX = 'onboarding-task:';

const ONBOARDING_GROUP_PREFIX = '__onboarding_checklist__';

/**
 * Legacy virtual group ids (before checklist rows used real task groups).
 * Still recognized so old handlers do not treat them as normal groups.
 */
export const ONBOARDING_TASK_GROUP_ID = `${ONBOARDING_GROUP_PREFIX}`;

const DEPT_ORDER: ProjectDepartment[] = [
  'preconstruction',
  'design',
  'procurement',
  'production',
  'installation',
];

/**
 * Preferred task group **names** (case-insensitive) to map template `department`
 * when `taskGroupId` is not set. Order = try first match in the project.
 */
const GROUP_NAME_CANDIDATES: Record<ProjectDepartment, string[]> = {
  preconstruction: ['Preconstruction'],
  design: ['Design'],
  /** Only real Procurement group — do not fall back to Preconstruction */
  procurement: ['Procurement'],
  production: ['Production'],
  installation: ['Installation', 'Delivery', 'Shipment'],
};

function normName(s: string): string {
  return s.trim().toLowerCase();
}

export function normalizeOnboardingDepartment(value: unknown): ProjectDepartment {
  const ok = (d: string): d is ProjectDepartment =>
    (DEPT_ORDER as string[]).includes(d);
  if (typeof value === 'string' && ok(value)) return value;
  return 'design';
}

/** True for legacy virtual umbrella / per-dept ids */
export function isOnboardingVirtualGroupId(groupId: string | undefined): boolean {
  if (!groupId) return false;
  if (groupId === ONBOARDING_TASK_GROUP_ID) return true;
  return DEPT_ORDER.some((d) => `${ONBOARDING_GROUP_PREFIX}${d}` === groupId);
}

export function isOnboardingBridgeTaskId(id: string | undefined): boolean {
  return !!id && id.startsWith(TASK_PREFIX);
}

/**
 * Parse synthetic onboarding checklist task ids.
 * - New (scoped): `onboarding-task:{projectId}:{templateItemId}` — unique per project; avoids comment/thread collisions.
 * - Legacy: `onboarding-task:{templateItemId}` only — same id across all projects (bug).
 */
export function parseOnboardingBridgeTaskId(taskId: string): { projectId: string | null; itemId: string } | null {
  if (!isOnboardingBridgeTaskId(taskId)) return null;
  const rest = taskId.slice(TASK_PREFIX.length);
  const firstColon = rest.indexOf(':');
  if (firstColon === -1) {
    return { projectId: null, itemId: rest };
  }
  return {
    projectId: rest.slice(0, firstColon),
    itemId: rest.slice(firstColon + 1),
  };
}

/** Checklist item id for Firestore `projectOnboarding` updates (template row id). */
export function onboardingTaskIdToItemId(taskId: string): string {
  const p = parseOnboardingBridgeTaskId(taskId);
  return p ? p.itemId : taskId;
}

/**
 * Firestore listeners for `taskComments` / `pdfAnnotations`: primary id plus legacy onboarding id scoped by project.
 */
export function getOnboardingCommentQuerySpecs(
  taskId: string,
  projectId: string | undefined,
): Array<{ taskId: string; projectId?: string }> {
  if (!isOnboardingBridgeTaskId(taskId)) {
    return [{ taskId }];
  }
  const parsed = parseOnboardingBridgeTaskId(taskId);
  if (!parsed) return [{ taskId }];

  if (parsed.projectId) {
    const scoped: Array<{ taskId: string; projectId?: string }> = [{ taskId }];
    if (projectId && projectId === parsed.projectId) {
      scoped.push({ taskId: `${TASK_PREFIX}${parsed.itemId}`, projectId });
    }
    return scoped;
  }

  if (projectId) {
    return [{ taskId, projectId }];
  }
  return [{ taskId }];
}

/**
 * Resolve a task row for Slack / notification / Forecast deep links (`?task=` or `openTaskId`).
 * Handles legacy onboarding ids (`onboarding-task:{itemId}`) vs scoped mirror ids.
 * Also accepts an id match when `projectId` on the row is missing (stale docs).
 */
export function findProjectTaskForDeepLink(
  taskId: string,
  projectId: string,
  tasks: ProjectTask[],
): ProjectTask | undefined {
  const tid = taskId.trim();
  if (!tid || !projectId) return undefined;

  const matchesProject = (t: ProjectTask) => !t.projectId || t.projectId === projectId;

  const direct = tasks.find((t) => t.id === tid && matchesProject(t));
  if (direct) return direct;

  if (!isOnboardingBridgeTaskId(tid)) return undefined;

  const parsed = parseOnboardingBridgeTaskId(tid);
  if (!parsed?.itemId) return undefined;

  if (parsed.projectId && parsed.projectId !== projectId) return undefined;

  if (!parsed.projectId) {
    const scopedId = `${TASK_PREFIX}${projectId}:${parsed.itemId}`;
    return tasks.find((t) => t.id === scopedId && matchesProject(t));
  }

  // Scoped onboarding id for this project — try legacy mirror id as a fallback.
  const legacyId = `${TASK_PREFIX}${parsed.itemId}`;
  return tasks.find((t) => t.id === legacyId && matchesProject(t));
}

function sortedGroupsForProject(projectId: string, projectGroups: TaskGroup[]): TaskGroup[] {
  return [...projectGroups]
    .filter((g) => g.projectId === projectId && g.id)
    .sort((a, b) => (a.position || 0) - (b.position || 0));
}

/**
 * Map a PM department to a task group id by group name (for any task, not only onboarding).
 * Does not default to "first group" — avoids sending Procurement work into Preconstruction.
 */
export function resolveTaskGroupIdForDepartment(
  department: ProjectDepartment,
  projectId: string,
  projectGroups: TaskGroup[],
): string | undefined {
  const sorted = sortedGroupsForProject(projectId, projectGroups);
  const dept = normalizeOnboardingDepartment(department);
  const candidates = GROUP_NAME_CANDIDATES[dept] || GROUP_NAME_CANDIDATES.design;

  for (const name of candidates) {
    const want = normName(name);
    const found = sorted.find((g) => normName(g.name) === want);
    if (found?.id) return found.id;
  }
  for (const name of candidates) {
    const want = normName(name);
    const found = sorted.find(
      (g) => normName(g.name).includes(want) || want.includes(normName(g.name)),
    );
    if (found?.id) return found.id;
  }
  return undefined;
}

/**
 * Infer PM department from a task group's display name (inverse of {@link resolveTaskGroupIdForDepartment}).
 * Used when a task is moved by group/column so department stays in sync.
 */
export function resolveDepartmentFromTaskGroupName(groupName: string): ProjectDepartment | undefined {
  const n = normName(groupName);
  for (const dept of DEPT_ORDER) {
    const candidates = GROUP_NAME_CANDIDATES[dept];
    for (const c of candidates) {
      if (normName(c) === n) return dept;
    }
  }
  for (const dept of DEPT_ORDER) {
    const candidates = GROUP_NAME_CANDIDATES[dept];
    for (const c of candidates) {
      const cn = normName(c);
      if (n.includes(cn) || cn.includes(n)) return dept;
    }
  }
  return undefined;
}

/**
 * Pick a real task group for a checklist item: explicit `taskGroupId` if valid,
 * else match `department` to group names (never arbitrary first group).
 */
export function resolveOnboardingItemGroupId(
  item: OnboardingItem,
  projectId: string,
  projectGroups: TaskGroup[],
): string | undefined {
  const sorted = sortedGroupsForProject(projectId, projectGroups);

  if (item.taskGroupId) {
    const hit = sorted.find((x) => x.id === item.taskGroupId);
    if (hit) return hit.id;
  }

  const dept = normalizeOnboardingDepartment(item.department);
  return resolveTaskGroupIdForDepartment(dept, projectId, projectGroups);
}

/**
 * Mirror every project's onboarding checklist items into virtual ProjectTasks —
 * the same mapping the Gantt uses. Schedule-derived computations (payment due
 * flags, ready-to-bill, cash flow) must use this so groups populated only by
 * checklist items (e.g. Production) still anchor correctly.
 */
export function mirrorAllOnboardingTasks(
  onboardings: Array<{ projectId?: string; items?: OnboardingItem[] }>,
  taskGroups: TaskGroup[],
): ProjectTask[] {
  const out: ProjectTask[] = [];
  for (const ob of onboardings) {
    if (!ob.projectId || !ob.items?.length) continue;
    for (const item of ob.items) {
      out.push(onboardingItemToProjectTask(item, ob.projectId, taskGroups));
    }
  }
  return out;
}

export function onboardingItemToProjectTask(
  item: OnboardingItem,
  projectId: string,
  projectGroups: TaskGroup[],
): ProjectTask {
  const dept = normalizeOnboardingDepartment(item.department);
  const now = new Date();
  const defaultEnd = item.completed && item.completedAt
    ? new Date(item.completedAt)
    : new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  const status: TaskStatus =
    item.taskStatus ?? (item.completed ? 'completed' : 'pending');
  const priority: TaskPriority = item.priority ?? (item.required ? 'high' : 'medium');
  const startDate = item.startDate ? new Date(item.startDate) : now;
  const endDate = item.endDate ? new Date(item.endDate) : defaultEnd;
  const edges = item.dependencyEdges ?? [];
  const progress =
    item.progress !== undefined && item.progress !== null
      ? item.progress
      : item.completed
        ? 100
        : 0;
  const groupId = resolveOnboardingItemGroupId(item, projectId, projectGroups);
  return {
    id: `${TASK_PREFIX}${projectId}:${item.id}`,
    projectId,
    groupId,
    title: item.title,
    description: item.description || '',
    department: dept,
    assignedTo:
      item.assignedTo && item.assignedTo.length > 0
        ? item.assignedTo
        : item.assigneeId
          ? [item.assigneeId]
          : [],
    status,
    priority,
    startDate,
    endDate,
    dependencies: edges.map((e) => e.predecessorId),
    dependencyEdges: edges.length ? edges : undefined,
    progress,
    position: item.order,
    notes: item.required ? 'Onboarding checklist (required)' : 'Onboarding checklist',
    subtasks: item.subtasks,
    actionRequired: item.actionRequired,
    actionRequiredNote: item.actionRequiredNote,
    onCriticalPath: item.onCriticalPath === true,
    scheduleLocked: item.scheduleLocked === true,
    lockedAt: item.lockedAt,
    lockedBy: item.lockedBy,
    actualStartDate: item.actualStartDate,
    actualEndDate: item.actualEndDate,
    completedAt: item.completedAt,
    supplierOrgIds: item.supplierOrgIds,
    supplierManual: item.supplierManual,
    scheduleNeedsPmReview: item.scheduleNeedsPmReview,
    scheduleReviewedByPmAt: item.scheduleReviewedByPmAt,
    scheduleReviewedByPmUid: item.scheduleReviewedByPmUid,
    createdAt: now,
    updatedAt: now,
  };
}
